import { UseFormReturn, useWatch } from "react-hook-form";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { FormPreview, FormProvider } from "@/pages/forms/types/form";
import { PROVIDER_LABEL, PROVIDERS } from "@/features/forms/constants";
import { FormWizardData } from "./wizard-form";

type Props = {
  form: UseFormReturn<FormWizardData>;
  preview: FormPreview | null;
  fetching: boolean;
  onFetch: () => void;

  onProviderChange: (p: FormProvider) => void;
};

export default function StepSource({
  form,
  preview,
  fetching,
  onFetch,
  onProviderChange,
}: Props) {
  const provider = useWatch({ control: form.control, name: "provider" });

  const tab = (p: FormProvider) => (
    <button
      key={p}
      type="button"
      onClick={() => onProviderChange(p)}
      className={`px-3 py-1.5 rounded-md text-sm border ${
        provider === p
          ? "bg-sidebar text-white border-sidebar"
          : "bg-white text-gray-600 border-gray-200"
      }`}
    >
      {PROVIDER_LABEL[p]}
    </button>
  );

  return (
    <div className="space-y-6">
      <div className="flex gap-2">{PROVIDERS.map(tab)}</div>

      <FormField
        control={form.control}
        name="source_id"
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              Source ID <span className="text-red-500">*</span>
            </FormLabel>
            <FormControl>
              <Input
                placeholder={`${PROVIDER_LABEL[provider]} form id`}
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Both providers expose their definition, so both are fetched the same
          way and neither takes a hand-typed name. */}
      <div className="space-y-3">
        <Button
          type="button"
          variant="outline"
          onClick={onFetch}
          disabled={fetching}
        >
          {fetching ? "Fetching…" : `Fetch from ${PROVIDER_LABEL[provider]}`}
        </Button>
        {preview && (
          <div className="rounded-md border bg-gray-50 px-4 py-3 text-sm text-gray-700">
            ✓ <span className="font-medium">{preview.title}</span> ·{" "}
            {preview.questionsCount} questions
            {preview.workspace_name
              ? ` · workspace: ${preview.workspace_name}`
              : ""}
          </div>
        )}
        <p className="text-xs text-gray-500">
          Name &amp; questions are pulled from {PROVIDER_LABEL[provider]} on
          save.
        </p>
      </div>
    </div>
  );
}
